/**
 * Real-time experience synchronization utility.
 *
 * Provides direct WebSocket-based sync for ephemeral UI state (e.g., slider positions,
 * toggle states) without involving session state or database writes.
 *
 * This is designed for high-frequency, low-latency sync scenarios where:
 * - Data is ephemeral and doesn't need persistence
 * - Session DB writes would be too costly or slow
 * - Real-time responsiveness is critical
 *
 * @example
 * ```typescript
 * // Initialize sync channel
 * const sync = createExperienceSync<IExperienceSyncState>({
 *   sessionId: userSession().get('id'),
 *   topicId: 'experience',
 * });
 *
 * // Connect and listen for updates
 * await sync.connect();
 * sync.on('sync', (data) => {
 *   console.log('Received sync data:', data);
 * });
 *
 * // Send updates (dealer only typically)
 * sync.send({ sliderPercentage: 75 });
 *
 * // Clean up
 * sync.disconnect();
 * ```
 */

import { z } from 'zod';
import { SignalerClient } from 'webrtc/signal';
import { EConnectionEvents, EExperienceSyncEvents, EWebsocketClientEvents, TWebSocketMessage } from 'webrtc/events';
import logger from 'utils/log';

// Experience sync schema for real-time micro-2d-app state synchronization between dealer and customer.
//
// EVERY field here is broadcast to every peer on the session+topic on EVERY update, not just when
// that field changes (AVS-4401 made sends full-state so a peer that missed a message is repaired
// by the next one). Keep it to presentation state: no PII, no tokens, no identifiers.
export const experienceSyncSchema = z.object({
  // Compare mode fields
  sliderPercentage: z.number().default(50), // Slider position as percentage (0-100)
  sliderEnabled: z.boolean().default(false), // Whether the comparison slider overlay is visible
  swapEnabled: z.boolean().default(false), // Whether the car positions are swapped
  sideBySideComparison: z.boolean().default(true), // Whether side-by-side comparison mode is active
  // Gallery mode fields
  currentImageIndex: z.number().default(0), // Currently displayed image index in gallery mode
});
export type IExperienceSyncState = z.infer<typeof experienceSyncSchema>;

/** Coalescing window for send() — rapid partial updates collapse into fewer WebSocket messages. */
export const SEND_THROTTLE_MS = 150;

/**
 * How long a buffered update may keep waiting for a usable socket before we give up on it
 * (AVS-4401). Bounded so a socket that never recovers cannot spin a timer for the life of the
 * session; the host's snapshot-on-reconnect is the backstop past this point.
 */
export const PENDING_SEND_MAX_WAIT_MS = 10_000;

export type TExperienceSyncEvents = EExperienceSyncEvents | EConnectionEvents;

export interface IExperienceSyncOptions<T> {
  /** Session ID to scope the sync channel */
  sessionId: string;
  /** Topic ID for the sync channel (defaults to 'experience') */
  topicId?: string;
  /** Initial state snapshot */
  initialState?: T;
  /** Zod schema for validation (optional but recommended) */
  schema?: z.ZodType<T>;
  /** Callback when connection is established */
  onConnect?: (connectionId: string) => void;
  /** Callback when connection is lost */
  onDisconnect?: () => void;
  /** Callback when an error occurs */
  onError?: (error: Error) => void;
}

export type TExperienceSyncCallback<T> = (data: T, from?: string) => void;

export interface IExperienceSyncChannel<T> {
  /** Connect to the sync channel */
  connect(): Promise<string>;
  /** Disconnect from the sync channel */
  disconnect(): void;
  /** Send partial state update to all peers */
  send(data: Partial<T>): void;
  /** Send full state snapshot (typically dealer -> customer on join) */
  sendSnapshot(data: T): void;
  /** Request full state from peers (typically customer -> dealer) */
  requestSnapshot(): void;
  /** Subscribe to sync events */
  on(event: 'sync', callback: TExperienceSyncCallback<Partial<T>>): void;
  on(event: 'snapshot', callback: TExperienceSyncCallback<T>): void;
  on(event: 'snapshotRequest', callback: (from: string) => void): void;
  on(event: 'peerConnected', callback: (connectionId: string) => void): void;
  on(event: 'connect', callback: (connectionId: string) => void): void;
  on(event: 'disconnect', callback: () => void): void;
  /** Unsubscribe from sync events */
  off(event: 'sync' | 'snapshot' | 'snapshotRequest' | 'peerConnected' | 'connect' | 'disconnect'): void;
  /** Check if connected */
  isConnected(): boolean;
  /** Get the connection ID */
  getConnectionId(): string | undefined;
}

type TInternalEvent = 'sync' | 'snapshot' | 'snapshotRequest' | 'peerConnected' | 'connect' | 'disconnect';

/**
 * Creates a real-time experience sync channel.
 *
 * @param options Configuration options
 * @returns Experience sync interface
 */
export function createExperienceSync<T extends Record<string, unknown>>(
  options: IExperienceSyncOptions<T>
): IExperienceSyncChannel<T> {
  const { sessionId, topicId = 'experience', schema, onConnect, onDisconnect, onError } = options;

  if (!sessionId) {
    throw new Error('[ExperienceSync] sessionId is required');
  }

  const wsApiUrl = `${process.env.SIGNALING_ORIGIN}?sessionId=${sessionId}&topicId=${topicId}`;

  let signalerClient: SignalerClient | null = null;
  let connectionId: string | undefined;
  // Use a Map for O(1) listener lookup and removal
  const listeners = new Map<TInternalEvent, Set<TExperienceSyncCallback<unknown>>>();

  // Throttle state for send() — coalesces rapid partial updates into fewer WebSocket messages
  let pendingSendData: Partial<T> | null = null;
  let sendThrottleTimer: ReturnType<typeof setTimeout> | null = null;
  let lastSendTime = 0;
  /** When the current buffer started waiting, for the PENDING_SEND_MAX_WAIT_MS deadline. */
  let pendingSinceTime = 0;

  function addListener<C>(event: TInternalEvent, callback: C): void {
    if (!listeners.has(event)) {
      listeners.set(event, new Set());
    }
    listeners.get(event)!.add(callback as TExperienceSyncCallback<unknown>);
  }

  function removeListener(event: TInternalEvent): void {
    listeners.delete(event);
  }

  function callListeners<D>(event: TInternalEvent, data: D, from?: string): void {
    const eventListeners = listeners.get(event);
    if (!eventListeners) return;

    for (const callback of eventListeners) {
      callback(data, from);
    }
  }

  function validatePartialData(data: unknown): Partial<T> | null {
    // For partial updates, only validate that the provided fields are correct types
    // Don't apply schema defaults - just pass through the exact fields that were sent
    if (!schema || typeof data !== 'object' || data === null) {
      return data as Partial<T>;
    }

    // Only keep the keys that were explicitly provided in the incoming data
    const incomingKeys = Object.keys(data);
    const result = schema.safeParse(data);

    if (!result.success) {
      logger.warn('[ExperienceSync] Invalid partial data received:', result.error);
      return null;
    }

    // Filter result to only include keys that were in the original data
    // This prevents Zod defaults from overwriting existing state
    const filteredResult: Partial<T> = {};
    for (const key of incomingKeys) {
      if (key in result.data) {
        (filteredResult as Record<string, unknown>)[key] = (result.data as Record<string, unknown>)[key];
      }
    }
    return filteredResult;
  }

  function validateFullData(data: unknown): T | null {
    if (!schema) return data as T;

    const result = schema.safeParse(data);
    if (!result.success) {
      logger.warn('[ExperienceSync] Invalid snapshot data received:', result.error);
      return null;
    }
    return result.data;
  }

  function setupListeners(): void {
    if (!signalerClient) return;

    logger.log('[ExperienceSync] Setting up listeners');

    // Handle incoming sync data (partial updates - don't apply defaults)
    signalerClient.on(EExperienceSyncEvents.SyncData, (data, from) => {
      const validData = validatePartialData(data);
      if (validData) {
        callListeners('sync', validData, from);
      }
    });

    // Handle incoming snapshot (full state - apply defaults)
    signalerClient.on(EExperienceSyncEvents.SyncSnapshot, (data, from) => {
      const validData = validateFullData(data);
      if (validData) {
        callListeners('snapshot', validData, from);
      }
    });

    // Handle snapshot request
    signalerClient.on(EExperienceSyncEvents.RequestSnapshot, (_, from) => {
      if (from) {
        callListeners('snapshotRequest', from, from);
      }
    });

    // Handle new peer connections (for host to send initial state)
    signalerClient.on(EConnectionEvents.ConnectionAdded, (data) => {
      logger.log('[ExperienceSync] ConnectionAdded event received:', data);
      const peerConnectionId = (data as { connectionId?: string })?.connectionId;
      if (peerConnectionId) {
        callListeners('peerConnected', peerConnectionId, peerConnectionId);
      }
    });

    // AVS-4382: bridge SignalerClient's own internal reconnect (a socket that dropped and
    // silently recovered via doReconnect() in webrtc/signal/index.ts) into the same 'connect'
    // notification the initial connect() above fires — unlike that initial connect, this never
    // calls connect() again, so without this listener host state changes made during the drop
    // are never re-broadcast on recovery.
    signalerClient.on(EWebsocketClientEvents.WsClientReconnected, (data) => {
      const newConnectionId = (data as { connectionId?: string } | undefined)?.connectionId;
      if (newConnectionId) {
        connectionId = newConnectionId;
      }
      if (connectionId) {
        logger.log(`[ExperienceSync] Reconnected with ID: ${connectionId}`);
        // A buffered update that could not go out during the drop goes now — this is the
        // earliest usable moment, and the host has no other trigger to re-send it (AVS-4401).
        //
        // Order matters: the flush MUST precede callListeners('connect'), because the host's
        // connect listener responds by sending an authoritative snapshot of its current state
        // (useExperienceSync.ts). Flushing first lets that snapshot supersede a buffer that may
        // be up to PENDING_SEND_MAX_WAIT_MS stale. Reversed, the stale buffer would land last
        // and win, and the wire has no sequence numbers to catch it.
        flushPendingSend();
        callListeners('connect', connectionId);
      }
    });
  }

  async function connect(): Promise<string> {
    if (signalerClient?.isReady()) {
      logger.log('[ExperienceSync] Already connected');
      return connectionId!;
    }

    signalerClient = new SignalerClient(wsApiUrl);
    setupListeners();

    const connection = await signalerClient.connect();
    connectionId = connection.connectionId;

    logger.log(`[ExperienceSync] Connected with ID: ${connectionId}`);

    callListeners('connect', connectionId);
    onConnect?.(connectionId);

    return connectionId;
  }

  function disconnect(): void {
    if (!signalerClient) return;

    // Cancel any pending throttled send
    if (sendThrottleTimer) {
      clearTimeout(sendThrottleTimer);
      sendThrottleTimer = null;
    }
    pendingSendData = null;

    signalerClient.disconnect();
    signalerClient = null;
    connectionId = undefined;

    callListeners('disconnect', undefined);
    onDisconnect?.();

    logger.log('[ExperienceSync] Disconnected');
  }

  function armFlushTimer(delay: number): void {
    if (sendThrottleTimer) return;
    sendThrottleTimer = setTimeout(() => {
      sendThrottleTimer = null;
      flushPendingSend();
    }, delay);
  }

  function flushPendingSend(): void {
    if (!pendingSendData) return;

    // The socket is not usable yet. Keep the buffered update and try again rather than
    // stranding it (AVS-4401): this is the host's most recent action, nothing else re-sends
    // it, and the old early-return left it parked with no timer — where the next leading-edge
    // send then discarded it outright.
    //
    // `isOpen()` rather than `isReady()`, which also accepts CONNECTING: handing the message to
    // a connecting socket only parks it in SignalerClient's outboundQueue, and a failed connect
    // recovers via doReconnect(), which drops that queue. The buffer would be empty and the
    // update gone. Keep waiting until it can actually leave the machine.
    if (!signalerClient?.isOpen()) {
      if (Date.now() - pendingSinceTime >= PENDING_SEND_MAX_WAIT_MS) {
        logger.warn('[ExperienceSync] Dropping a buffered update; the socket never came back');
        pendingSendData = null;
        return;
      }
      armFlushTimer(SEND_THROTTLE_MS);
      return;
    }

    const data = pendingSendData;
    pendingSendData = null;
    lastSendTime = Date.now();

    const message: TWebSocketMessage = {
      action: EExperienceSyncEvents.SyncData as unknown as TWebSocketMessage['action'],
      data: data as Record<string, unknown>,
    };
    signalerClient.signal(message);
  }

  function send(data: Partial<T>): void {
    if (!signalerClient?.isReady()) {
      onError?.(new Error('[ExperienceSync] Not connected'));
      return;
    }

    const now = Date.now();
    const elapsed = now - lastSendTime;

    if (elapsed >= SEND_THROTTLE_MS) {
      // Outside throttle window — send immediately (leading edge). Carry any still-buffered
      // update along instead of dropping it: nulling the buffer here used to silently lose an
      // update that a socket blip had stranded (AVS-4401).
      const payload = pendingSendData ? { ...pendingSendData, ...data } : data;
      pendingSendData = null;
      lastSendTime = now;

      const message: TWebSocketMessage = {
        action: EExperienceSyncEvents.SyncData as unknown as TWebSocketMessage['action'],
        data: payload as Record<string, unknown>,
      };
      signalerClient.signal(message);
    } else {
      // Within throttle window — merge into pending buffer
      if (!pendingSendData) {
        pendingSinceTime = now;
      }
      pendingSendData = pendingSendData ? { ...pendingSendData, ...data } : { ...data };
      armFlushTimer(SEND_THROTTLE_MS - elapsed);
    }
  }

  function sendSnapshot(data: T): void {
    if (!signalerClient?.isReady()) {
      onError?.(new Error('[ExperienceSync] Not connected'));
      return;
    }

    const message: TWebSocketMessage = {
      action: EExperienceSyncEvents.SyncSnapshot as unknown as TWebSocketMessage['action'],
      data: data as Record<string, unknown>,
    };

    signalerClient.signal(message);
  }

  function requestSnapshot(): void {
    if (!signalerClient?.isReady()) {
      onError?.(new Error('[ExperienceSync] Not connected'));
      return;
    }

    const message: TWebSocketMessage = {
      action: EExperienceSyncEvents.RequestSnapshot as unknown as TWebSocketMessage['action'],
      data: {},
    };

    signalerClient.signal(message);
  }

  function isConnected(): boolean {
    return signalerClient?.isReady() ?? false;
  }

  function getConnectionId(): string | undefined {
    return connectionId;
  }

  return {
    connect,
    disconnect,
    send,
    sendSnapshot,
    requestSnapshot,
    on: addListener as IExperienceSyncChannel<T>['on'],
    off: removeListener,
    isConnected,
    getConnectionId,
  };
}
